import React, { useState, useEffect } from 'react';
import { Package, Component, DollarSign, Users, Star } from 'lucide-react';
import DashboardStatCard from './DashboardStatCard'; // Re-used StatCard
import { productService } from '../../../services/productService'; // Main productService
import { componentService } from '../../../services/componentService'; // Main componentService
import { sellerService } from '../../../services/sellerService'; // NEW: sellerService
import { sellerProductService } from '../../../services/sellerProductService'; // NEW: sellerProductService
import { checkoutService } from '../../../services/checkoutService'; // Aligned with Statements subtab
import { supabase } from '../../../lib/supabase'; // NEW: supabase
import { useAuth } from '../../../contexts/AuthContext'; // To get authenticated user ID
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

export default function SellerDashboardStats({ sellerId: propSellerId, activeTab }) {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalComponents: 0,
    totalRevenue: 0,
    totalSold: 0,
    storeVisits: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadSellerDashboardStats(user.id, propSellerId);
    } else {
      setLoading(false);
      setError('You must be logged in to view your dashboard stats.');
    }
  }, [user?.id, propSellerId, activeTab]);

  const loadSellerDashboardStats = async (userId, propId = null) => {
    try {
      if (!stats.totalProducts && !stats.totalSold && !stats.totalComponents) {
        setLoading(true);
      }
      setError('');

      // 1. Resolve Seller Account ID and views count safely
      let sellerId = propId;
      let sellerAccount = null;

      const { data: accData } = await supabase
        .from('seller_accounts')
        .select('id, views_count, user_id')
        .or(`user_id.eq.${userId}${sellerId ? `,id.eq.${sellerId}` : ''}`)
        .maybeSingle();
      
      if (accData) {
        sellerAccount = accData;
        if (!sellerId) sellerId = accData.id;
      }

      let totalProducts = 0;
      let validProducts = [];
      let totalComponents = 0;
      let totalRevenue = 0;
      let totalSold = 0;
      const storeVisits = sellerAccount?.views_count || 0;

      // 2. Fetch products using sellerProductService if sellerId exists
      if (sellerId) {
        const sellerProducts = await sellerProductService.getSellerProductsBySellerId(sellerId);
        validProducts = (sellerProducts || []).filter(p => p.status !== 'deleted');
        totalProducts = validProducts.length;
      } else {
        // Fallback to legacy method
        const productResult = await productService?.getAll({ createdBy: userId });
        if (productResult?.products) {
          totalProducts = productResult.products.length;
        }
      }

      // 3. Fetch components using sellerId (accurate, multi-tenant count)
      if (sellerId) {
        const componentResults = await componentService?.getAll({ sellerId: sellerId });
        if (componentResults) {
          const validComponents = componentResults.filter(c => c.status !== 'deleted');
          totalComponents = validComponents.length || 0;
        }
      }

      // 4. Calculate Total Sold and My Revenue from seller orders (aligned with Statements subtab)
      const currentSellerId = sellerId || userId;
      let sellerOrders = [];

      try {
        if (currentSellerId) {
          sellerOrders = await checkoutService.getSellerOrders(currentSellerId);
        }
      } catch (ordErr) {
        console.warn('Notice fetching seller orders via checkoutService in SellerDashboardStats:', ordErr);
      }

      if (sellerOrders && sellerOrders.length > 0) {
        sellerOrders.forEach(ord => {
          (ord.items || []).forEach(it => {
            const qty = parseInt(it.quantity) || 1;
            const lineTotal = parseFloat(it.total_price) || ((parseFloat(it.price || it.unit_price) || 0) * qty);
            totalSold += qty;
            totalRevenue += lineTotal;
          });
        });
      } else if (currentSellerId) {
        // Fallback: If checkoutService returned no orders, query order_items directly (matching SellerPaymentDetails fallback)
        try {
          const { data: itemsData } = await supabase
            .from('order_items')
            .select(`
              id,
              order_id,
              seller_id,
              quantity,
              unit_price,
              total_price,
              item_status
            `)
            .or(`seller_id.eq.${currentSellerId},seller_id.eq.${userId}`);

          if (itemsData && itemsData.length > 0) {
            itemsData.forEach(it => {
              const qty = parseInt(it.quantity) || 1;
              const lineTotal = parseFloat(it.total_price) || ((parseFloat(it.unit_price) || 0) * qty);
              totalSold += qty;
              totalRevenue += lineTotal;
            });
          }
        } catch (itemErr) {
          console.warn('Notice querying fallback order_items in SellerDashboardStats:', itemErr);
        }
      }

      // Safe fallback to legacy fields if no order records exist yet
      if (totalSold === 0 && totalRevenue === 0 && validProducts.length > 0) {
        const legacySold = validProducts.reduce((sum, p) => sum + (p.qtySold || 0), 0);
        const legacyRev = validProducts.reduce((sum, p) => sum + ((parseFloat(p.price) || 0) * (p.qtySold || 0)), 0);
        if (legacySold > 0) totalSold = legacySold;
        if (legacyRev > 0) totalRevenue = legacyRev;
      }

      setStats({
        totalProducts,
        totalComponents,
        totalRevenue,
        totalSold,
        storeVisits
      });

    } catch (err) {
      console.error('Load seller dashboard stats error:', err);
      setError(err?.message || 'Failed to load your dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        <p className="ml-3 text-muted-foreground">Loading your stats...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-destructive mb-1">Error Loading Stats</h3>
            <p className="text-sm text-muted-foreground">{error}</p>
            {user?.id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadSellerDashboardStats(user.id, propSellerId)}
                className="mt-3"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
      <DashboardStatCard
        title="My Products"
        value={stats?.totalProducts}
        icon={<Package className="w-6 h-6" />}
        color="blue"
      />
      <DashboardStatCard
        title="My Components"
        value={stats?.totalComponents}
        icon={<Component className="w-6 h-6" />}
        color="green"
      />
      <DashboardStatCard
        title="Store Visits"
        value={stats?.storeVisits}
        icon={<Icon name="Eye" size={24} />}
        color="cyan"
      />
      <DashboardStatCard
        title="Total Sold"
        value={stats?.totalSold}
        icon={<Users className="w-6 h-6" />}
        color="purple"
      />
      <DashboardStatCard
        title="My Revenue"
        value={`$${(stats?.totalRevenue || 0).toFixed(2)}`}
        icon={<DollarSign className="w-6 h-6" />}
        color="orange"
      />
      <DashboardStatCard
        title="Community Score"
        value="4.8"
        icon={<Star className="w-6 h-6" />}
        color="yellow"
      />
    </div>
  );
}